"use client";

import type { CSSProperties, ReactNode } from "react";
import * as React from "react";
import { cn } from "../../lib";

export type GradualBlurPosition = "top" | "bottom" | "left" | "right";

export type GradualBlurCurve =
	| "linear"
	| "bezier"
	| "ease-in"
	| "ease-out"
	| "ease-in-out";

export type GradualBlurPreset =
	| "top"
	| "bottom"
	| "left"
	| "right"
	| "subtle"
	| "intense"
	| "smooth"
	| "sharp"
	| "header"
	| "footer"
	| "sidebar"
	| "page-header"
	| "page-footer";

export interface GradualBlurProps {
	children?: ReactNode;
	position?: GradualBlurPosition;
	strength?: number;
	height?: string;
	width?: string;
	divCount?: number;
	exponential?: boolean;
	zIndex?: number;
	animated?: boolean | "scroll";
	duration?: string;
	easing?: string;
	opacity?: number;
	curve?: GradualBlurCurve;
	responsive?: boolean;
	mobileHeight?: string;
	tabletHeight?: string;
	desktopHeight?: string;
	mobileWidth?: string;
	tabletWidth?: string;
	desktopWidth?: string;
	preset?: GradualBlurPreset;
	/** Reserved for future GPU hints; currently unused. */
	gpuOptimized?: boolean;
	hoverIntensity?: number;
	target?: "parent" | "page";
	onAnimationComplete?: () => void;
	className?: string;
	style?: CSSProperties;
}

const DEFAULT_CONFIG: Partial<GradualBlurProps> = {
	position: "bottom",
	strength: 2,
	height: "6rem",
	divCount: 5,
	exponential: false,
	zIndex: 1000,
	animated: false,
	duration: "0.3s",
	easing: "ease-out",
	opacity: 1,
	curve: "linear",
	responsive: false,
	target: "parent",
	className: "",
	style: {},
};

export const GRADUAL_BLUR_PRESETS: Record<string, Partial<GradualBlurProps>> = {
	top: { position: "top", height: "6rem" },
	bottom: { position: "bottom", height: "6rem" },
	left: { position: "left", height: "6rem" },
	right: { position: "right", height: "6rem" },

	subtle: { height: "4rem", strength: 1, opacity: 0.8, divCount: 3 },
	intense: { height: "10rem", strength: 4, divCount: 8, exponential: true },

	smooth: { height: "8rem", curve: "bezier", divCount: 10 },
	sharp: { height: "5rem", curve: "linear", divCount: 4 },

	header: { position: "top", height: "8rem", curve: "ease-out" },
	footer: { position: "bottom", height: "8rem", curve: "ease-out" },
	sidebar: { position: "left", height: "6rem", strength: 2.5 },

	"page-header": {
		position: "top",
		height: "10rem",
		target: "page",
		strength: 3,
	},
	"page-footer": {
		position: "bottom",
		height: "10rem",
		target: "page",
		strength: 3,
	},
};

/** @deprecated Use `GRADUAL_BLUR_PRESETS` */
export const PRESETS = GRADUAL_BLUR_PRESETS;

export const GRADUAL_BLUR_CURVE_FUNCTIONS: Record<
	GradualBlurCurve,
	(p: number) => number
> = {
	linear: (p) => p,
	bezier: (p) => p * p * (3 - 2 * p),
	"ease-in": (p) => p * p,
	"ease-out": (p) => 1 - (1 - p) ** 2,
	"ease-in-out": (p) => (p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2),
};

/** @deprecated Use `GRADUAL_BLUR_CURVE_FUNCTIONS` */
export const CURVE_FUNCTIONS = GRADUAL_BLUR_CURVE_FUNCTIONS;

function mergeConfigs(
	...configs: Partial<GradualBlurProps>[]
): Partial<GradualBlurProps> {
	const acc: Partial<GradualBlurProps> = {};
	for (const config of configs) {
		Object.assign(acc, config);
	}
	return acc;
}

function getGradientDirection(position: string): string {
	const directions: Record<string, string> = {
		top: "to top",
		bottom: "to bottom",
		left: "to left",
		right: "to right",
	};
	return directions[position] ?? "to bottom";
}

function debounce<T extends (...args: unknown[]) => void>(
	fn: T,
	wait: number,
): (...args: Parameters<T>) => void {
	let t: ReturnType<typeof setTimeout> | undefined;
	return (...args: Parameters<T>) => {
		if (t !== undefined) {
			clearTimeout(t);
		}
		t = setTimeout(() => fn(...args), wait);
	};
}

function useResponsiveAxis(
	responsive: boolean | undefined,
	base: string | undefined,
	mobile: string | undefined,
	tablet: string | undefined,
	desktop: string | undefined,
): string | undefined {
	const [val, setVal] = React.useState(base);

	React.useEffect(() => {
		if (!responsive || typeof window === "undefined") {
			return;
		}
		const calc = () => {
			const w = window.innerWidth;
			let next = base;
			if (w <= 480 && mobile) {
				next = mobile;
			} else if (w <= 768 && tablet) {
				next = tablet;
			} else if (w <= 1024 && desktop) {
				next = desktop;
			}
			setVal(next);
		};
		const deb = debounce(calc, 100);
		calc();
		window.addEventListener("resize", deb);
		return () => window.removeEventListener("resize", deb);
	}, [responsive, base, mobile, tablet, desktop]);

	return responsive ? val : base;
}

function useIntersectionObserver(
	containerRef: React.RefObject<HTMLDivElement | null>,
	shouldObserve: boolean,
): boolean {
	const [isVisible, setIsVisible] = React.useState(!shouldObserve);

	React.useLayoutEffect(() => {
		if (!shouldObserve) {
			return;
		}
		const node = containerRef.current;
		if (!node) {
			return;
		}
		const observer = new IntersectionObserver(
			([entry]) => setIsVisible(entry.isIntersecting),
			{ threshold: 0.1 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef, shouldObserve]);

	return isVisible;
}

interface MergedGradualBlurConfig extends Partial<GradualBlurProps> {
	position: GradualBlurPosition;
	strength: number;
	height: string;
	width?: string;
	divCount: number;
	exponential: boolean;
	zIndex: number;
	animated: boolean | "scroll";
	duration: string;
	easing: string;
	opacity: number;
	curve: GradualBlurCurve;
	responsive: boolean;
	target: "parent" | "page";
	className: string;
	style: CSSProperties;
	hoverIntensity?: number;
	onAnimationComplete?: () => void;
}

function mergePropsToConfig(props: GradualBlurProps): MergedGradualBlurConfig {
	const { children: _omitChildren, ...rest } = props;
	const presetConfig =
		props.preset && GRADUAL_BLUR_PRESETS[props.preset]
			? GRADUAL_BLUR_PRESETS[props.preset]
			: {};
	const merged = mergeConfigs(DEFAULT_CONFIG, presetConfig, rest);
	return merged as MergedGradualBlurConfig;
}

/** Stable key for inline `style` objects so memo deps ignore reference churn. */
function gradualBlurStyleKey(style: CSSProperties | undefined): string {
	if (style == null || Object.keys(style).length === 0) {
		return "";
	}
	try {
		return JSON.stringify(style);
	} catch {
		return "";
	}
}

function useMergedGradualBlurConfig(
	props: GradualBlurProps,
): MergedGradualBlurConfig {
	return React.useMemo(
		() => mergePropsToConfig(props),
		[
			props.preset,
			props.position,
			props.strength,
			props.height,
			props.width,
			props.divCount,
			props.exponential,
			props.zIndex,
			props.animated,
			props.duration,
			props.easing,
			props.opacity,
			props.curve,
			props.responsive,
			props.mobileHeight,
			props.tabletHeight,
			props.desktopHeight,
			props.mobileWidth,
			props.tabletWidth,
			props.desktopWidth,
			props.gpuOptimized,
			props.hoverIntensity,
			props.target,
			props.className,
			gradualBlurStyleKey(props.style),
		],
	);
}

function GradualBlurInner(props: GradualBlurProps) {
	const { children } = props;
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [isHovered, setIsHovered] = React.useState(false);
	const onAnimationCompleteRef = React.useRef(props.onAnimationComplete);
	onAnimationCompleteRef.current = props.onAnimationComplete;

	const config = useMergedGradualBlurConfig(props);

	const responsiveHeight = useResponsiveAxis(
		config.responsive,
		config.height,
		config.mobileHeight,
		config.tabletHeight,
		config.desktopHeight,
	);

	const responsiveWidth = useResponsiveAxis(
		config.responsive,
		config.width,
		config.mobileWidth,
		config.tabletWidth,
		config.desktopWidth,
	);

	const isVisible = useIntersectionObserver(
		containerRef,
		config.animated === "scroll",
	);

	const blurDivs = React.useMemo(() => {
		const divs: React.ReactNode[] = [];
		const increment = 100 / config.divCount;
		const currentStrength =
			isHovered && config.hoverIntensity
				? config.strength * config.hoverIntensity
				: config.strength;

		const curveFunc =
			GRADUAL_BLUR_CURVE_FUNCTIONS[config.curve] ??
			GRADUAL_BLUR_CURVE_FUNCTIONS.linear;

		for (let i = 1; i <= config.divCount; i++) {
			let progress = i / config.divCount;
			progress = curveFunc(progress);

			let blurValue: number;
			if (config.exponential) {
				blurValue = 2 ** (progress * 4) * 0.0625 * currentStrength;
			} else {
				blurValue =
					0.0625 * (progress * config.divCount + 1) * currentStrength;
			}

			const p1 = Math.round((increment * i - increment) * 10) / 10;
			const p2 = Math.round(increment * i * 10) / 10;
			const p3 = Math.round((increment * i + increment) * 10) / 10;
			const p4 = Math.round((increment * i + increment * 2) * 10) / 10;

			let gradient = `transparent ${p1}%, black ${p2}%`;
			if (p3 <= 100) {
				gradient += `, black ${p3}%`;
			}
			if (p4 <= 100) {
				gradient += `, transparent ${p4}%`;
			}

			const direction = getGradientDirection(config.position);

			const divStyle: CSSProperties = {
				maskImage: `linear-gradient(${direction}, ${gradient})`,
				WebkitMaskImage: `linear-gradient(${direction}, ${gradient})`,
				backdropFilter: `blur(${blurValue.toFixed(3)}rem)`,
				opacity: config.opacity,
				transition:
					config.animated && config.animated !== "scroll"
						? `backdrop-filter ${config.duration} ${config.easing}`
						: undefined,
			};

			divs.push(
				<div key={i} className="absolute inset-0" style={divStyle} />,
			);
		}

		return divs;
	}, [config, isHovered]);

	const containerStyle: CSSProperties = React.useMemo(() => {
		const isVertical = ["top", "bottom"].includes(config.position);
		const isHorizontal = ["left", "right"].includes(config.position);
		const isPageTarget = config.target === "page";

		const baseStyle: CSSProperties = {
			position: isPageTarget ? "fixed" : "absolute",
			pointerEvents: config.hoverIntensity ? "auto" : "none",
			opacity: isVisible ? 1 : 0,
			transition: config.animated
				? `opacity ${config.duration} ${config.easing}`
				: undefined,
			zIndex: isPageTarget ? config.zIndex + 100 : config.zIndex,
			...config.style,
		};

		if (isVertical) {
			baseStyle.height = responsiveHeight;
			baseStyle.width = responsiveWidth || "100%";
			baseStyle[config.position] = 0;
			baseStyle.left = 0;
			baseStyle.right = 0;
		} else if (isHorizontal) {
			baseStyle.width = responsiveWidth || responsiveHeight;
			baseStyle.height = "100%";
			baseStyle[config.position] = 0;
			baseStyle.top = 0;
			baseStyle.bottom = 0;
		}

		return baseStyle;
	}, [config, responsiveHeight, responsiveWidth, isVisible]);

	React.useEffect(() => {
		if (isVisible && config.animated === "scroll") {
			const ms = Number.parseFloat(config.duration) * 1000;
			const t = globalThis.window?.setTimeout(() => {
				onAnimationCompleteRef.current?.();
			}, ms);
			return () => {
				if (t !== undefined) {
					clearTimeout(t);
				}
			};
		}
		return undefined;
	}, [isVisible, config.animated, config.duration]);

	const hoverHandlers = config.hoverIntensity
		? {
				onMouseEnter: () => setIsHovered(true),
				onMouseLeave: () => setIsHovered(false),
			}
		: {};

	return (
		<div
			ref={containerRef}
			className={cn(
				"gradual-blur relative isolate",
				config.target === "page"
					? "gradual-blur-page"
					: "gradual-blur-parent",
				config.className,
			)}
			style={containerStyle}
			{...hoverHandlers}
		>
			<div className="relative h-full w-full">{blurDivs}</div>
			{children ? <div className="relative">{children}</div> : null}
		</div>
	);
}

export const GradualBlur = React.memo(GradualBlurInner);
GradualBlur.displayName = "GradualBlur";

export default GradualBlur;

function injectGradualBlurStyles(): void {
	if (typeof document === "undefined") {
		return;
	}
	const id = "gradual-blur-styles";
	if (document.getElementById(id)) {
		return;
	}
	const el = document.createElement("style");
	el.id = id;
	el.textContent =
		".gradual-blur{pointer-events:none;transition:opacity .3s ease-out}.gradual-blur-inner{pointer-events:none}";
	document.head.appendChild(el);
}

if (typeof document !== "undefined") {
	injectGradualBlurStyles();
}
